(() => {
  'use strict';
  if (customElements.get('exfum-pause-app')) return;
  const activities = {
    square: { category: 'calm', title: 'Respiration carrée', text: 'Suivez quatre temps doux, sans forcer. Respirez librement si une pause ne vous convient pas.', duration: 48, phases: [['Inspirez', 3, true], ['Retenez doucement', 3, true], ['Expirez', 3, false], ['Pause', 3, false]], cycles: 4 },
    long: { category: 'calm', title: 'Expiration longue', text: 'Inspirez doucement, puis laissez l’expiration durer un peu plus longtemps, à votre rythme.', duration: 45, phases: [['Inspirez', 3, true], ['Expirez', 6, false]], cycles: 5 },
    five: { category: 'calm', title: '5 respirations', text: 'Cinq allers-retours, tranquillement. Adaptez le rythme à votre confort.', duration: 40, phases: [['Inspirez', 4, true], ['Expirez', 4, false]], cycles: 5 },
    colors: { category: 'mind', title: 'Trouve la couleur', text: 'Touchez les 5 cases ambrées, marquées d’un soleil ☀. Trois petites grilles, sans chronomètre.', time: '20–45 s' },
    count: { category: 'mind', title: 'Compte rapide', text: 'Quelques formes, trois réponses possibles. Prenez votre temps pendant trois manches.', time: '20–45 s' },
    memory: { category: 'mind', title: 'Mémoire express', text: 'Observez une suite de symboles, puis reproduisez-la dans l’ordre. Trois manches de 3, 4 puis 5 symboles.', time: '30–60 s' },
    water: { category: 'move', title: 'Quelques gorgées', text: 'Prenez quelques gorgées d’eau.', duration: 20 },
    place: { category: 'move', title: 'Changer d’endroit', text: 'Levez-vous et changez de pièce ou de place pendant une minute, si vous le pouvez.', duration: 60 },
    hands: { category: 'move', title: 'Occuper les mains', text: 'Prenez un objet autour de vous et manipulez-le pendant 30 secondes.', duration: 30 }
  };
  const symbols = ['●', '▲', '■', '◆'];
  const names = ['Cercle', 'Triangle', 'Carré', 'Losange'];
  const escape = value => String(value).replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  const button = (label, action, value = '', style = 'secondary') => '<button type="button" class="exfum-pause-' + style + '" data-exfum-pause-action="' + action + '" data-exfum-pause-value="' + escape(value) + '">' + label + '</button>';
  const shuffle = values => {
    for (let i = values.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [values[i], values[j]] = [values[j], values[i]]; }
    return values;
  };
  class ExfumPause extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.view = this.querySelector('[data-exfum-pause-view]');
      if (!this.view) { this.controller = null; return; }
      if (!this.homeHTML) {
        this.homeHTML = this.view.innerHTML;
        this.titles = Object.fromEntries([...this.view.querySelectorAll('[data-exfum-pause-action="category"]')].map(el => [el.dataset.exfumPauseValue, el.querySelector('strong').textContent]));
      }
      this.state = { screen: 'home' };
      this.addEventListener('click', event => this.click(event), options);
      window.addEventListener('popstate', event => {
        const state = event.state && event.state['exfum-pause'];
        this.state = this.valid(state) ? state : { screen: 'home' };
        this.render();
      }, options);
      window.addEventListener('pagehide', () => this.stop(), options);
      window.addEventListener('pageshow', event => { if (event.persisted) this.render(); }, options);
      document.addEventListener('visibilitychange', () => { this.lastTick = performance.now(); }, options);
      this.saveHistory(false);
      this.render(false);
      this.emit('view');
    }
    emit(name, detail = {}) {
      this.dispatchEvent(new CustomEvent('exfum_pause_' + name, { bubbles: true, detail }));
    }
    disconnectedCallback() { this.stop(); this.controller?.abort(); this.controller = null; }
    valid(state) {
      return state && (['home', 'community'].includes(state.screen) ||
        (state.screen === 'category' && Object.hasOwn(this.titles, state.category)) ||
        (['activity', 'complete'].includes(state.screen) && Object.hasOwn(activities, state.id)));
    }
    stop() { clearInterval(this.timer); this.timer = null; this.phaseIndex = -1; }
    saveHistory(push) {
      try {
        const state = { ...(history.state || {}), 'exfum-pause': this.state };
        history[push ? 'pushState' : 'replaceState'](state, '');
      } catch (_) {}
    }
    go(state, push = true) { this.state = state; this.saveHistory(push); this.render(); }
    title(title, eyebrow = 'VOTRE PETITE PAUSE') {
      return '<p class="exfum-pause-eyebrow">' + escape(eyebrow) + '</p><h1 class="exfum-pause-title" tabindex="-1">' + escape(title) + '</h1>';
    }
    focus() { this.view.querySelector('h1')?.focus({ preventScroll: true }); this.scrollIntoView({ block: 'start', behavior: 'instant' }); }
    nav() {
      return '<nav class="exfum-pause-nav" aria-label="Navigation de la pause">' + button('← Retour', 'back', '', 'text-button') + button('Accueil', 'home', '', 'text-button') + '</nav>';
    }
    render(moveFocus = true) {
      this.stop();
      this.running = false;
      const state = this.state;
      let html = this.nav();
      if (state.screen === 'home') html = this.homeHTML;
      if (state.screen === 'community') html += this.querySelector('[data-exfum-pause-community]').innerHTML;
      if (state.screen === 'category') {
        const category = state.category;
        const intros = { calm: 'Un rythme à suivre, sans rien réussir ni forcer.', mind: 'Une courte activité pour porter votre attention ailleurs.', move: 'Un petit geste concret, là où vous êtes.' };
        html += this.title(this.titles[category], 'CHOISISSEZ VOTRE PAUSE') + '<p class="exfum-pause-intro">' + intros[category] + '</p><div class="exfum-pause-cards">';
        for (const [id, activity] of Object.entries(activities).filter(([, a]) => a.category === category)) {
          html += '<button type="button" class="exfum-pause-card" data-exfum-pause-action="activity" data-exfum-pause-value="' + id + '"><span class="exfum-pause-icon" aria-hidden="true">' + ({ calm: '◯', mind: '◇', move: '⌁' }[category]) + '</span><span><strong>' + activity.title + '</strong><small>' + (activity.time || 'Environ ' + activity.duration + ' s') + '</small></span><span aria-hidden="true">↗</span></button>';
        }
        html += '</div>';
      }
      if (state.screen === 'activity') {
        const activity = activities[state.id];
        html += this.title(activity.title) + '<p class="exfum-pause-intro">' + activity.text + '</p><p class="exfum-pause-caption">' + (activity.time || 'Environ ' + activity.duration + ' secondes') + '</p>';
        html += '<div data-exfum-pause-stage></div><div class="exfum-pause-actions" data-exfum-pause-controls>' + button('Démarrer', 'start', '', 'primary') + '</div>';
      }
      if (state.screen === 'complete') {
        html += this.title('C’est passé un peu ?', activities[state.id].category === 'mind' ? 'BIEN JOUÉ.' : 'VOUS AVEZ PRIS CE MOMENT.');
        html += '<p class="exfum-pause-intro">Comment vous sentez-vous, maintenant ?</p><div class="exfum-pause-actions">' + button('Oui', 'feedback', 'yes', 'primary') + button('Un peu', 'feedback', 'some') + button('Pas vraiment', 'feedback', 'no') + '</div><div class="exfum-pause-actions">' + button('Refaire', 'activity', state.id, 'text-button') + button('Choisir autre chose', 'category', activities[state.id].category, 'text-button') + '</div>';
      }
      this.view.innerHTML = html;
      if (moveFocus) this.focus();
    }
    click(event) {
      const communityLink = event.target.closest('a.exfum-pause-primary[href]');
      if (this.state.screen === 'community' && communityLink && this.contains(communityLink)) this.emit('discord_click');
      const target = event.target.closest('[data-exfum-pause-action]');
      if (!target || !this.contains(target) || target.disabled) return;
      const action = target.dataset.exfumPauseAction, value = target.dataset.exfumPauseValue;
      if (action === 'home') return this.go({ screen: 'home' });
      if (action === 'community') return this.go({ screen: 'community' });
      if (action === 'category' && Object.hasOwn(this.titles, value)) {
        this.emit('category_click', { category_id: value });
        return this.go({ screen: 'category', category: value });
      }
      if (action === 'activity' && Object.hasOwn(activities, value)) {
        if (this.state.screen === 'complete' && this.state.id === value) this.emit('activity_restart', { activity_id: value, category_id: activities[value].category });
        return this.go({ screen: 'activity', id: value });
      }
      if (action === 'back' || action === 'stop') {
        const a = activities[this.state.id];
        return this.go(a ? { screen: 'category', category: a.category } : { screen: 'home' });
      }
      if (action === 'feedback' && this.state.screen === 'complete') {
        const category = activities[this.state.id].category;
        this.emit('feeling', { activity_id: this.state.id, category_id: category, feeling: value });
        if (value === 'yes') return this.go({ screen: 'home' });
        if (value === 'some') return this.go({ screen: 'category', category });
        this.go({ screen: 'home' });
        this.view.querySelector('.exfum-pause-intro').textContent = 'Vous pouvez essayer une autre catégorie, à votre rythme.';
        const previous = this.view.querySelector('[data-exfum-pause-value="' + category + '"]');
        if (previous) previous.hidden = true;
        return;
      }
      if (this.state.screen !== 'activity') return;
      if (action === 'start' && !this.running) return this.start();
      if (action === 'done' && this.running) return this.complete();
      if (action === 'next' && this.running) { this.round++; return this.game(); }
      if (action === 'recall' && this.running && this.state.id === 'memory') return this.recall();
      if (action === 'show' && this.running && this.state.id === 'memory') return this.showSequence();
      if (action === 'tile' && this.running && this.state.id === 'colors' && this.accepting) {
        if (target.dataset.exfumPauseTarget !== 'true') { this.status('Cherchez les cases avec un soleil ☀.'); return; }
        target.disabled = true; target.textContent = '✓'; target.setAttribute('aria-label', 'Case trouvée');
        this.found++;
        this.status(this.found + ' sur 5 trouvées.');
        if (this.found === 5) this.roundDone();
      }
      if (action === 'answer' && this.running && this.accepting && this.state.id === 'count') {
        if (Number(value) === this.answer) this.roundDone();
        else this.status('Prenez votre temps, comptez encore une fois.');
      }
      if (action === 'symbol' && this.running && this.accepting && this.state.id === 'memory') {
        if (Number(value) !== this.sequence[this.entered]) {
          this.entered = 0; this.updateRecall();
          this.status('On reprend depuis le début. Vous pouvez revoir la suite.');
        } else {
          this.entered++; this.updateRecall();
          this.status(this.entered + ' symbole' + (this.entered > 1 ? 's' : '') + ' sur ' + this.sequence.length + '.');
          if (this.entered === this.sequence.length) this.roundDone();
        }
      }
    }
    stage() { return this.view.querySelector('[data-exfum-pause-stage]'); }
    controls(html) { this.view.querySelector('[data-exfum-pause-controls]').innerHTML = html; }
    status(message) { const el = this.view.querySelector('[data-exfum-pause-status]'); if (el) el.textContent = message; }
    complete() {
      this.emit('activity_complete', { activity_id: this.state.id, category_id: activities[this.state.id].category });
      this.go({ screen: 'complete', id: this.state.id }, false);
    }
    start() {
      this.running = true;
      const activity = activities[this.state.id];
      this.emit('activity_start', { activity_id: this.state.id, category_id: activity.category });
      this.controls(button('Arrêter et revenir', 'stop', '', 'text-button'));
      if (activity.category === 'mind') { this.round = 0; this.game(); }
      else if (activity.category === 'calm') this.breathe(activity);
      else this.micro(activity);
    }
    clock(duration, update, finish) {
      this.stop(); let elapsed = 0;
      this.lastTick = performance.now(); update(0);
      this.timer = setInterval(() => {
        const now = performance.now(), delta = now - this.lastTick;
        this.lastTick = now;
        if (document.hidden) return;
        elapsed = Math.min(duration, elapsed + Math.min(delta, 500) / 1000);
        update(elapsed);
        if (elapsed >= duration) { this.stop(); finish(); }
      }, 100);
    }
    progress() { return '<progress class="exfum-pause-progress" data-exfum-pause-progress max="100" value="0" aria-label="Progression de l’activité"></progress>'; }
    breathe(activity) {
      this.stage().innerHTML = '<div class="exfum-pause-center"><div class="exfum-pause-orbit"><div class="exfum-pause-disc" aria-hidden="true" data-exfum-pause-disc><span>◯</span></div></div><p class="exfum-pause-instruction" role="status" aria-live="polite" data-exfum-pause-status></p><p class="exfum-pause-caption" data-exfum-pause-cycle></p>' + this.progress() + '</div>';
      const disc = this.view.querySelector('[data-exfum-pause-disc]');
      const cycleDuration = activity.phases.reduce((sum, phase) => sum + phase[1], 0);
      this.clock(activity.duration, elapsed => {
        const cycle = Math.min(activity.cycles - 1, Math.floor(elapsed / cycleDuration));
        let position = elapsed % cycleDuration, phaseIndex = 0;
        while (phaseIndex < activity.phases.length - 1 && position >= activity.phases[phaseIndex][1]) { position -= activity.phases[phaseIndex][1]; phaseIndex++; }
        const index = cycle * activity.phases.length + phaseIndex;
        if (index !== this.phaseIndex) {
          this.phaseIndex = index;
          const phase = activity.phases[phaseIndex];
          this.status(phase[0]);
          disc.style.setProperty('--exfum-pause-phase', phase[1] + 's');
          disc.dataset.exfumPauseExpanded = String(phase[2]);
          this.view.querySelector('[data-exfum-pause-cycle]').textContent = 'Respiration ' + (cycle + 1) + ' sur ' + activity.cycles;
        }
        this.view.querySelector('[data-exfum-pause-progress]').value = elapsed / activity.duration * 100;
      }, () => this.complete());
    }
    micro(activity) {
      this.stage().innerHTML = '<div class="exfum-pause-panel exfum-pause-center"><p class="exfum-pause-caption">Un instant pour ce geste</p><div class="exfum-pause-timer" role="timer" aria-label="Secondes restantes" data-exfum-pause-timer>' + activity.duration + ' s</div>' + this.progress() + '</div>';
      this.controls(button('C’est fait', 'done', '', 'primary') + button('Arrêter et revenir', 'stop', '', 'text-button'));
      this.clock(activity.duration, elapsed => {
        this.view.querySelector('[data-exfum-pause-timer]').textContent = Math.ceil(activity.duration - elapsed) + ' s';
        this.view.querySelector('[data-exfum-pause-progress]').value = elapsed / activity.duration * 100;
      }, () => this.complete());
    }
    game() {
      this.stop(); this.accepting = true;
      this.controls(button('Arrêter et revenir', 'stop', '', 'text-button'));
      const id = this.state.id;
      const round = '<p class="exfum-pause-caption">Manche ' + (this.round + 1) + ' sur 3</p>';
      const status = '<p class="exfum-pause-status" role="status" aria-live="polite" data-exfum-pause-status></p>';
      if (id === 'colors') {
        this.found = 0;
        const cells = shuffle([...Array(16).keys()]).map(n => {
          const isTarget = n < 5;
          return '<button type="button" class="exfum-pause-tile" data-exfum-pause-action="tile" data-exfum-pause-target="' + isTarget + '" aria-label="' + (isTarget ? 'Case ambrée, soleil' : 'Case verte, cercle') + '">' + (isTarget ? '☀' : '○') + '</button>';
        }).join('');
        this.stage().innerHTML = round + '<h2 class="exfum-pause-instruction">Touchez les 5 cases ambrées.</h2><p class="exfum-pause-caption">Elles portent un soleil ☀.</p><div class="exfum-pause-grid">' + cells + '</div>' + status;
      }
      if (id === 'count') {
        this.answer = 4 + Math.floor(Math.random() * 6);
        const shape = symbols[this.round];
        const options = shuffle([this.answer - 1, this.answer, this.answer + 1]);
        this.stage().innerHTML = round + '<h2 class="exfum-pause-instruction">Combien en voyez-vous ?</h2><div class="exfum-pause-shapes" role="group" aria-label="Formes à compter">' + Array.from({ length: this.answer }, () => '<span role="img" aria-label="' + names[this.round] + '">' + shape + '</span>').join('') + '</div><div class="exfum-pause-choices">' + options.map(n => button(String(n), 'answer', n)).join('') + '</div>' + status;
      }
      if (id === 'memory') {
        this.sequence = Array.from({ length: this.round + 3 }, () => Math.floor(Math.random() * 4));
        this.showSequence();
      }
      this.stage().setAttribute('tabindex', '-1');
      this.stage().focus({ preventScroll: true });
    }
    roundDone() {
      this.accepting = false;
      this.stage().querySelectorAll('button').forEach(el => { el.disabled = true; });
      this.status('Bien joué.');
      const last = this.round === 2;
      this.controls(button(last ? 'Terminer' : 'Manche suivante', last ? 'done' : 'next', '', 'primary') + button('Arrêter et revenir', 'stop', '', 'text-button'));
      this.view.querySelector('[data-exfum-pause-controls] button').focus({ preventScroll: true });
    }
    showSequence() {
      this.stop(); this.accepting = false; this.entered = 0;
      this.stage().innerHTML = '<p class="exfum-pause-caption">Manche ' + (this.round + 1) + ' sur 3</p><h2 class="exfum-pause-instruction">Observez la suite.</h2><p class="exfum-pause-caption">Elle se masquera dans 10 secondes. Vous pourrez la revoir.</p><div class="exfum-pause-shapes" role="img" aria-label="' + this.sequence.map(i => names[i]).join(', puis ') + '">' + this.sequence.map(i => '<span aria-hidden="true">' + symbols[i] + '</span>').join('') + '</div>';
      this.controls(button('Je suis prêt·e', 'recall', '', 'primary') + button('Arrêter et revenir', 'stop', '', 'text-button'));
      this.clock(10, () => {}, () => this.recall());
    }
    recall() {
      this.stop(); this.accepting = true; this.entered = 0;
      this.stage().innerHTML = '<p class="exfum-pause-caption">Manche ' + (this.round + 1) + ' sur 3</p><h2 class="exfum-pause-instruction">À vous de retrouver l’ordre.</h2><div class="exfum-pause-shapes" aria-label="Votre réponse" data-exfum-pause-recall></div><div class="exfum-pause-choices">' + symbols.map((s, i) => '<button type="button" data-exfum-pause-action="symbol" data-exfum-pause-value="' + i + '" aria-label="' + names[i] + '">' + s + '</button>').join('') + '</div><p class="exfum-pause-status" role="status" data-exfum-pause-status></p>';
      this.updateRecall();
      this.controls(button('Revoir la suite', 'show') + button('Arrêter et revenir', 'stop', '', 'text-button'));
      this.stage().setAttribute('tabindex', '-1'); this.stage().focus({ preventScroll: true });
    }
    updateRecall() {
      const el = this.view.querySelector('[data-exfum-pause-recall]');
      el.textContent = this.sequence.map((s, i) => i < this.entered ? symbols[s] : '·').join(' ');
      el.setAttribute('aria-label', this.entered + ' symboles saisis sur ' + this.sequence.length);
    }
  }
  customElements.define('exfum-pause-app', ExfumPause);
})();
